import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * <product-upsell-card>
 * A locked upsell tile (see snippets/product-upsell-card.liquid). The "Add" pill
 * adds the variant to the cart, then toggles to a -/+ stepper that sets the cart
 * line quantity. All cart writes go through the standard AJAX cart routes and
 * refresh the rest of the UI by dispatching the theme's cart:update event, which
 * cart-items-component re-renders from (it re-fetches when no sections are passed).
 *
 * @extends {Component}
 */
class ProductUpsellCard extends Component {
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.qty = parseInt(this.dataset.inCart || '0', 10) || 0;
    this.addEventListener('click', this.#onClick);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  get variantId() {
    return this.dataset.variantId;
  }

  /** @param {Event} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const add = event.target.closest('[data-upsell-add]');
    const inc = event.target.closest('[data-upsell-inc]');
    const dec = event.target.closest('[data-upsell-dec]');
    if (!add && !inc && !dec) return;

    // The "View" fallback (bundle-builder products) is a real link — let it navigate.
    if (add && add.tagName === 'A') return;

    event.preventDefault();
    if (this.#busy) return;

    if (add) this.#add();
    else if (inc) this.#setQty(this.qty + 1);
    else if (dec) this.#setQty(this.qty - 1);
  };

  async #add() {
    this.#begin();
    try {
      const body = JSON.stringify({
        items: [
          {
            id: Number(this.variantId),
            quantity: 1,
            properties: { _source: this.dataset.source || 'Product Upsell' },
          },
        ],
      });
      const response = await fetch(`${Theme.routes.cart_add_url}`, fetchConfig('json', { body }));
      const data = await response.json();
      if (data.status) throw new Error(data.description || data.message || 'Add to cart failed');
      // Optimistic: we added one unit to this variant's line.
      this.qty += 1;
      this.#refreshCart();
    } catch (error) {
      console.error('[product-upsell-card] add failed', error);
    } finally {
      this.#end();
    }
  }

  /** @param {number} next */
  async #setQty(next) {
    if (next < 0) next = 0;
    this.#begin();
    try {
      const body = JSON.stringify({ id: this.variantId, quantity: next });
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const cart = await response.json();
      const item = (cart.items || []).find((i) => String(i.variant_id) === String(this.variantId));
      this.qty = item ? item.quantity : 0;
      this.#refreshCart(cart);
    } catch (error) {
      console.error('[product-upsell-card] quantity change failed', error);
    } finally {
      this.#end();
    }
  }

  /** @param {Object} [cart] */
  #refreshCart(cart) {
    document.dispatchEvent(
      new CartUpdateEvent(cart || {}, this.id || 'product-upsell-card', {
        source: 'product-upsell-card',
        variantId: this.variantId,
      })
    );
  }

  /**
   * Reconcile this card when the cart changes elsewhere (and the event carries a
   * full cart object). Our own writes already updated local state, so skip those.
   * @param {CartUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    if (event?.detail?.data?.source === 'product-upsell-card') return;
    const cart = event?.detail?.resource;
    if (!cart || !Array.isArray(cart.items)) return;
    const item = cart.items.find((i) => String(i.variant_id) === String(this.variantId));
    this.qty = item ? item.quantity : 0;
    this.#render();
  };

  #begin() {
    this.#busy = true;
    this.toggleAttribute('data-busy', true);
  }

  #end() {
    this.#busy = false;
    this.toggleAttribute('data-busy', false);
    this.#render();
  }

  #render() {
    this.classList.toggle('is-added', this.qty > 0);
    const num = this.querySelector('[data-upsell-num]');
    if (num) num.textContent = String(this.qty > 0 ? this.qty : 1);
  }
}

if (!customElements.get('product-upsell-card')) {
  customElements.define('product-upsell-card', ProductUpsellCard);
}
